/* @flow */
import idx from 'idx';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import * as React from 'react';
import { graphql, compose } from 'react-apollo';
import cx from 'classnames';

import Button from 'components/Button.jsx';
import Buttons from 'components/Buttons.jsx';
import Glyph from 'components/Glyph.jsx';
import Group from 'entities/Group';
import GroupAvatar from 'groups/components/GroupAvatar.jsx';
import GroupNavigationTabs from 'groups/components/GroupNavigationTabs.jsx';
import GroupSettingsModal from 'groups/components/GroupSettingsModal.jsx';
import GroupSubscribedSettingModal from 'groups/components/GroupSubscribedSettingModal.jsx';
import InviteButton from 'users/components/InviteButton.jsx';
import MediaObject from 'components/MediaObject.jsx';
import ModalTrigger from 'components/ModalTrigger.jsx';
import SchoolSettingsModal from 'groups/components/SchoolSettingsModal.jsx';
import Text from 'components/Text.jsx';

import './styles/GroupViewHeader.scss';

function getSettingModal(group: Group, isTeacher: boolean, router) {
  const onCodeChange = code => router.push(`/classes/${code}`);
  const onLeave = () => {
    router.push('/classes');
  };

  if (group.isStaffClass()) {
    return <SchoolSettingsModal group={group} onLeave={onLeave} />;
  }

  if (group.canEdit()) {
    return (
      <GroupSettingsModal group={group} onCodeChange={onCodeChange} userIsTeacher={isTeacher} />
    );
  }

  return <GroupSubscribedSettingModal group={group} />;
}

type Props = {
  group: Group,
  groupViewHeaderUser: ReactApolloData<GroupViewHeaderUserQuery>,
  groupViewHeaderAdminConsoleAccess: ReactApolloData<GroupViewHeaderAdminConsoleAccessQuery>,
};

class GroupViewHeader extends React.Component<Props> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  render() {
    const { group, groupViewHeaderUser, groupViewHeaderAdminConsoleAccess } = this.props;
    const { user } = groupViewHeaderUser;

    if (groupViewHeaderAdminConsoleAccess && groupViewHeaderAdminConsoleAccess.loading) {
      return <div className="GroupViewHeader-loading" />;
    }

    const adminConsoleAccess = idx(
      groupViewHeaderAdminConsoleAccess,
      _ => _.organizations[0].permissions.adminConsole.accessConsole
    );

    const isTeacher = user ? user.role === 'teacher' : false;
    const peopleTab = this.context.router.location.pathname.match(/classes\/\w+\/people/);
    const adminViewingPaidStaffClass = group.isStaffClass() && adminConsoleAccess;
    const hideSettings = group.isRoot() || adminViewingPaidStaffClass;

    const groupInfo = (
      <div className="GroupViewHeader-left-group-names">
        {group && (
          <GroupAvatar className="GroupViewHeader-left-group-logo" group={group} size="medium" />
        )}
        <div>
          <Text size="xlarge" element="div" color={adminViewingPaidStaffClass ? 'white' : 'pitch'}>
            {group.getName()}
          </Text>
          {!group.isRoot() &&
            !(group.getType() === 'district') && (
              <Text element="div" color={adminViewingPaidStaffClass ? 'wolf' : 'thunder'}>
                @{group.getCodeOrError()}
              </Text>
            )}
        </div>
      </div>
    );

    return (
      <div
        className={cx('GroupViewHeader', {
          'GroupViewHeader--dark': adminViewingPaidStaffClass,
        })}>
        <MediaObject figure="right" className="GroupViewHeader-top">
          <div className="GroupViewHeader-left">
            <div className="GroupViewHeader-left-group">
              {hideSettings ? (
                groupInfo
              ) : (
                <div>
                  <ModalTrigger
                    data-test="settings-modal"
                    modal={getSettingModal(group, isTeacher, this.context.router)}>
                    {groupInfo}
                  </ModalTrigger>
                </div>
              )}
            </div>
          </div>
          <div className="GroupViewHeader-right">
            <Buttons>
              {group.canAddPeople() && (
                <InviteButton
                  level={peopleTab ? 'primary' : 'secondary'}
                  forceType="button"
                  group={group}
                  color={adminViewingPaidStaffClass ? 'white' : 'brand'}
                />
              )}
              {hideSettings ? null : (
                <ModalTrigger modal={getSettingModal(group, isTeacher, this.context.router)}>
                  <Button size="small" level="secondary" color="polar-bear" aria-label="Settings">
                    <Glyph
                      solo
                      name="gear"
                      size="large"
                      data-test-selector="GroupViewHeader-settings-icon"
                    />
                  </Button>
                </ModalTrigger>
              )}
            </Buttons>
          </div>
        </MediaObject>
        <div className="GroupViewHeader-tabs">
          <GroupNavigationTabs adminTheme={adminViewingPaidStaffClass} group={group} />
        </div>
      </div>
    );
  }
}

const withGroupOrganizationPermissions = graphql(
  gql`
    query GroupViewHeaderAdminConsoleAccess($id: Int) {
      organizations(ids: [$id]) {
        id
        uuid
        permissions {
          adminConsole {
            accessConsole
          }
        }
      }
    }
  `,
  {
    name: 'groupViewHeaderAdminConsoleAccess',
    options: ({ group }) => ({
      variables: { id: group.getStaffClassOrgId() },
    }),
    skip: ({ group }) => !group.isStaffClass(),
  }
);

const withUserData = graphql(
  gql`
    query GroupViewHeaderUser {
      user: me {
        id
        uuid
        role
      }
    }
  `,
  {
    name: 'groupViewHeaderUser',
  }
);

export { GroupViewHeader };
export default compose(
  withGroupOrganizationPermissions,
  withUserData
)(GroupViewHeader);
